import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { Eye, EyeOff, Calendar, Activity, ArrowLeft, Fingerprint } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Link, Navigate } from "react-router-dom";
import { useLogin } from "@/hooks/auth/useLogin";
import { useAuth } from "@/contexts/AuthContext";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import vetAssistLogo from "@/assets/logo.webp";
import loginImage from "@/assets/bgdoctor-1.webp";
import PasskeyLoginButton from "@/components/auth/passkey/PasskeyLoginButton";
import { isWebAuthnSupported } from "@/utils/webauthn";
import TurnstileWidget from "@/components/shared/TurnstileWidget";
import { toast } from "sonner";

const loginSchema = z.object({
  email: z.string().min(1, "Email address is required").email("Please enter a valid email"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
  rememberMe: z.boolean().default(false),
});

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

export default function Login() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const { mutateAsync: login, isPending } = useLogin();
  // eslint-disable-next-line no-unused-vars
  const [focusedField, setFocusedField] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const isTurnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);
  const isPasskeySupported = isWebAuthnSupported();
  const [authMode, setAuthMode] = useState(isPasskeySupported ? "passkey" : "password");

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FDFBF7]">
        <Spinner className="h-8 w-8 text-[#2546A1]" />
      </div>
    );
  }

  if (isAuthenticated) {
    const role = user?.role;
    return <Navigate to={role === "admin" ? "/admin/transition" : "/resources"} replace />;
  }

  const resetTurnstile = () => {
    if (!isTurnstileEnabled) return;

    setTurnstileToken("");
    setTurnstileResetKey((prev) => prev + 1);
    turnstileRef.current?.reset?.();
  };

  const onSubmit = async (data) => {
    if (isTurnstileEnabled && !turnstileToken) {
      toast.error("Please complete the captcha verification before signing in.");
      return;
    }

    try {
      await login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        turnstileToken,
      });
    } catch (error) {
      // useLogin.onError already handles user-facing error feedback.
      // Catching here prevents uncaught Promise errors in the console.
    } finally {
      resetTurnstile();
    }
  };

  return (
    <>
      {/* STRICT: h-screen and overflow-hidden ensures no scrolling */}
      <div className="relative flex h-screen w-full items-center justify-center overflow-hidden bg-[#FDFBF7] p-3 pt-3 font-sans sm:p-4 sm:pt-4 lg:p-6 lg:pt-6">
        {/* Background Blobs (Scaled down for mobile) */}
        <div className="pointer-events-none absolute top-[-10%] left-[-5%] h-50 w-50 rounded-full bg-blue-100/50 blur-3xl md:h-100 md:w-100 lg:h-150 lg:w-150" />
        <div className="pointer-events-none absolute right-[-5%] bottom-[-10%] h-50 w-50 rounded-full bg-cyan-100/50 blur-3xl md:h-100 md:w-100 lg:h-150 lg:w-150" />

        {/* Main Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative z-10 flex h-auto w-full max-w-400 flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl shadow-gray-200/50 lg:h-[85vh] lg:max-h-212.5 lg:flex-row lg:rounded-[3rem]">
          {/* LEFT SIDE - Form Section */}
          {/* Adjusted padding and alignment for compact fit */}
          <div
            className={`relative flex h-full w-full flex-col items-center justify-start bg-white p-3 pt-4 sm:p-4 sm:pt-5 lg:w-[50%] lg:justify-center ${
              authMode === "passkey" ? "lg:p-7 xl:p-10" : "lg:p-6 xl:p-8"
            }`}>
            {/* Logo Pill - Compact on Mobile */}
            <div className="mb-3 lg:absolute lg:top-6 lg:left-8 lg:mb-0 xl:top-8">
              <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm lg:gap-2.5 lg:px-5 lg:py-2.5">
                <img src={vetAssistLogo} alt="VetAssist Logo" className="h-5 w-auto lg:h-8" />
              </div>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className={`w-full pb-2 lg:pt-2 ${
                authMode === "passkey"
                  ? "max-w-sm sm:max-w-md lg:max-w-xl"
                  : "max-w-xs sm:max-w-sm lg:max-w-md"
              }`}>
              {/* Header */}
              <motion.div
                variants={itemVariants}
                className={`text-center ${authMode === "passkey" ? "mb-4 lg:mb-5" : "mb-3 lg:mb-4"}`}>
                <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl lg:mb-1.5 lg:text-3xl">
                  Welcome back
                </h1>
                <p className="text-xs text-gray-500 sm:text-sm lg:text-base">
                  {authMode === "passkey"
                    ? "Sign in quickly using your passkey"
                    : "Sign in to access your clinic dashboard"}
                </p>
              </motion.div>

              {authMode === "passkey" && isPasskeySupported ? (
                <div className="relative space-y-4 overflow-hidden rounded-[2rem] border border-[#d8e8fa] bg-linear-to-b from-[#eaf3fd] via-[#f2f8ff] to-[#eaf8f0] px-5 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_25px_70px_rgba(37,70,161,0.12)] sm:px-6 sm:py-6 lg:space-y-5">
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute top-[22%] left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-blue-300/28 blur-3xl" />
                    <div className="absolute right-[12%] bottom-[16%] h-34 w-34 rounded-full bg-cyan-300/25 blur-2xl" />
                  </div>

                  <motion.div
                    variants={itemVariants}
                    className="relative z-10 flex justify-center pt-1 pb-2 lg:pt-2">
                    <div className="relative flex h-36 w-36 items-center justify-center rounded-full border-2 border-white/95 bg-linear-to-br from-[#e0eeff] via-[#f0f7ff] to-[#daf8e9] shadow-[0_0_0_4px_rgba(255,255,255,0.92),0_16px_44px_rgba(37,70,161,0.24)]">
                      <div className="absolute inset-2.5 rounded-full border border-white/85 bg-white/35" />
                      <Fingerprint className="relative z-10 h-12 w-12 text-[#1f2c46]" />
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants} className="relative z-10 text-center">
                    <h2 className="text-[2rem] leading-tight font-bold tracking-tight text-[#1f2a44] sm:text-[2.2rem]">
                      Intelligent Sign In
                    </h2>
                    <p className="mt-1 text-base font-medium text-[#62789a]">
                      Fast, secure, and passwordless
                    </p>
                  </motion.div>

                  <motion.div variants={itemVariants} className="relative z-10 pt-2">
                    <div className="mx-auto max-w-140 rounded-2xl p-0.5 shadow-[0_0_40px_rgba(56,189,248,0.25)]">
                      <PasskeyLoginButton mode="hero" />
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants} className="relative z-10 pt-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="h-px flex-1 bg-[#cfdff1]" />
                      <span className="text-[11px] font-semibold tracking-[0.16em] text-[#89a0bd] uppercase">
                        Alternative
                      </span>
                      <div className="h-px flex-1 bg-[#cfdff1]" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setAuthMode("password")}
                      className="mt-3 w-full text-center text-base font-semibold text-[#5f7596] transition-colors hover:text-[#2546A1] hover:underline">
                      Sign in with Password
                    </button>
                  </motion.div>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-2.5 sm:space-y-3 lg:space-y-3.5">
                    {/* Email Field */}
                    <motion.div variants={itemVariants}>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field, fieldState }) => (
                          <FormItem className="space-y-1">
                            <div className="ml-1 text-left text-xs font-semibold text-gray-700 lg:mb-1 lg:text-sm">
                              Email
                            </div>
                            <FormControl>
                              <div className="group relative">
                                <Input
                                  {...field}
                                  type="text"
                                  onFocus={() => setFocusedField("email")}
                                  onBlur={() => setFocusedField(null)}
                                  placeholder="test@gmail.com"
                                  className={`h-10 w-full rounded-full border-0 bg-gray-100/80 px-4 text-sm text-gray-900 transition-all duration-300 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-[#2546A1]/20 sm:h-11 sm:text-sm lg:h-12 lg:px-6 lg:text-base ${
                                    fieldState.error ? "bg-red-50 ring-2 ring-red-100" : ""
                                  }`}
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="ml-2 text-[10px] text-red-500 sm:text-xs" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    {/* Password Field */}
                    <motion.div variants={itemVariants}>
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field, fieldState }) => (
                          <FormItem className="space-y-1">
                            <div className="ml-1 text-left text-xs font-semibold text-gray-700 lg:mb-1 lg:text-sm">
                              Password
                            </div>
                            <FormControl>
                              <div className="group relative">
                                <Input
                                  {...field}
                                  type={showPassword ? "text" : "password"}
                                  onFocus={() => setFocusedField("password")}
                                  onBlur={() => setFocusedField(null)}
                                  placeholder="••••••••••••"
                                  className={`h-10 w-full rounded-full border-0 bg-gray-100/80 px-4 pr-10 text-sm text-gray-900 transition-all duration-300 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-[#2546A1]/20 sm:h-11 sm:text-sm lg:h-12 lg:px-6 lg:pr-11 lg:text-base ${
                                    fieldState.error ? "bg-red-50 ring-2 ring-red-100" : ""
                                  }`}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 lg:right-4 lg:p-1.5">
                                  {showPassword ? (
                                    <EyeOff className="h-4 w-4 lg:h-5 lg:w-5" />
                                  ) : (
                                    <Eye className="h-4 w-4 lg:h-5 lg:w-5" />
                                  )}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage className="ml-2 text-[10px] text-red-500 sm:text-xs" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    {/* Remember Me & Forgot Password */}
                    <motion.div
                      variants={itemVariants}
                      className="flex items-center justify-between px-1 lg:px-1.5">
                      <FormField
                        control={form.control}
                        name="rememberMe"
                        render={({ field }) => (
                          <FormItem className="flex cursor-pointer flex-row items-center space-y-0 space-x-2 lg:space-x-2.5">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="h-4 w-4 rounded-md border-gray-300 data-[state=checked]:border-[#2546A1] data-[state=checked]:bg-[#2546A1] lg:h-4.5 lg:w-4.5"
                              />
                            </FormControl>
                            <label
                              htmlFor="rememberMe"
                              className="cursor-pointer text-xs font-medium text-gray-500 select-none hover:text-gray-800 lg:text-xs"
                              onClick={() => field.onChange(!field.value)}>
                              Remember me
                            </label>
                          </FormItem>
                        )}
                      />
                      <Link
                        to="#"
                        className="text-xs font-semibold text-[#2546A1] hover:underline lg:text-xs">
                        Forgot Password?
                      </Link>
                    </motion.div>

                    {/* Sign In Button */}
                    <motion.div variants={itemVariants} className="pt-1.5 lg:pt-2">
                      <motion.button
                        type="submit"
                        disabled={isPending || (isTurnstileEnabled && !turnstileToken)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-[#2546A1] py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition-all duration-300 hover:bg-[#1e3a8a] disabled:cursor-not-allowed disabled:opacity-70 sm:py-3 sm:text-sm lg:py-3 lg:text-base">
                        {isPending ? (
                          <>
                            <Spinner className="h-4 w-4 text-white/90 lg:h-6 lg:w-6" />
                            <span>Signing in...</span>
                          </>
                        ) : (
                          "Sign In"
                        )}
                      </motion.button>
                    </motion.div>

                    {isTurnstileEnabled && (
                      <motion.div
                        variants={itemVariants}
                        className="flex flex-col items-start gap-1.5">
                        <p className="text-xs font-medium text-gray-500">
                          Verify that you are human
                        </p>
                        <TurnstileWidget
                          ref={turnstileRef}
                          className="w-full"
                          action="auth_login"
                          resetKey={turnstileResetKey}
                          onTokenChange={setTurnstileToken}
                          onError={(message) => toast.error(message)}
                        />
                      </motion.div>
                    )}

                    {isPasskeySupported && (
                      <motion.div variants={itemVariants}>
                        <div className="flex items-center gap-2.5">
                          <div className="h-px flex-1 bg-gray-200" />
                          <span className="text-xs font-medium text-gray-400">or</span>
                          <div className="bg-gray-20 h-px flex-1" />
                        </div>

                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setAuthMode("passkey")}
                            className="w-full rounded-full border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 sm:py-3 lg:py-3 lg:text-base">
                            Use passkey instead
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </form>
                </Form>
              )}

              <motion.div variants={itemVariants} className="pt-2 text-center lg:pt-3">
                <div className="flex justify-center gap-4 text-[10px] text-gray-400 sm:gap-5 sm:text-xs lg:gap-6 lg:text-xs">
                  <Link to="/terms" className="hover:text-gray-600">
                    Terms & Conditions
                  </Link>
                  <Link to="/privacy" className="hover:text-gray-600">
                    Privacy Policy
                  </Link>
                </div>

                <div className="mt-2.5 flex justify-center lg:mt-4">
                  <Link
                    to="/"
                    className="flex items-center gap-2 text-xs font-semibold text-[#2546A1] transition-colors hover:underline lg:text-sm">
                    <ArrowLeft className="h-3 w-3 lg:h-4 lg:w-4" />
                    Go back to Home
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* RIGHT SIDE - Image Section (Hidden on Mobile/Tablet) */}
          <div className="relative hidden bg-white lg:block lg:w-[50%]">
            <div className="relative h-full w-full overflow-hidden rounded-tl-[4rem] rounded-bl-[4rem]">
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={loginImage}
                  alt="Login Background"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-linear-to-br from-black/20 via-black/10 to-black/60"></div>
              </div>

              {/* Floating Widgets */}
              <div className="absolute top-0 left-0 flex h-full w-full flex-col justify-between p-12 xl:p-16">
                {/* Top Widget */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="self-end rounded-2xl bg-white/90 p-4 shadow-xl backdrop-blur-md xl:p-5">
                  <div className="flex items-center gap-3 xl:gap-4">
                    <div className="rounded-full bg-blue-100 p-2 xl:p-3">
                      <Calendar className="h-5 w-5 text-[#2546A1] xl:h-6 xl:w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500">System Status</p>
                      <p className="text-sm font-bold text-gray-800 xl:text-base">
                        All systems operational
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Content Bottom */}
                <div className="space-y-6 xl:space-y-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="max-w-xl">
                    <h2 className="text-4xl leading-tight font-bold text-white drop-shadow-md xl:text-5xl">
                      Streamline your clinic, <br />
                      <span className="text-blue-200">go completely paperless.</span>
                    </h2>
                    <p className="mt-3 text-sm font-medium tracking-wide text-white/90 drop-shadow-sm xl:mt-4 xl:text-base">
                      Tailored for PH clinics by Endsofttech Web Solutions.
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex w-fit items-center gap-4 rounded-[2rem] bg-white/20 p-2 pr-6 backdrop-blur-md xl:gap-5 xl:p-3 xl:pr-8">
                    <div className="flex -space-x-3 pl-2 xl:-space-x-4">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-10 w-10 rounded-full border-2 border-white bg-gray-200 xl:h-12 xl:w-12"
                          style={{
                            backgroundImage: `url(https://i.pravatar.cc/100?img=${i + 20})`,
                            backgroundSize: "cover",
                          }}
                        />
                      ))}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white xl:text-base">
                        Vet Assist Platform
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-green-300 xl:gap-2">
                        <Activity className="h-3 w-3 xl:h-4 xl:w-4" />
                        <span>100% Modern System</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
